import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../../components/Flex";
import Input from "../../../../../../../../../../components/Input";
import Text from "../../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../../components/app/CustomTitle";
import { Form2Payload } from "../../../../../../../scheduling/appointment/encounter/encounter-constant";
import CptCodesField from "../../components/form-components/CptCodesField";
import IcdCodesField from "../../components/form-components/IcdCodesField";

interface SolutionFocusedTherapyFormProps {
  formik: FormikProps<Form2Payload>;
}

const SolutionFocusedTherapyForm = ({
  formik,
}: SolutionFocusedTherapyFormProps) => {
  return (
    <>
      {/* Future-Focused & Miracle Question */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Future-Focused & Miracle Question</CustomTitle>
      </Flex>
      <Grid size={12}>
        <CustomTextArea
          label="Preferred Future/Goals"
          rows={3}
          name="preferredFutureGoals"
          placeholder="Client's vision of desired future, how things will be different"
          onBlur={formik.handleBlur}
          value={formik.values?.preferredFutureGoals ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid size={12}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Miracle Question Response",
            name: "miracleQuestionResponse",
            placeholder:
              "'Suppose tonight while you sleep, a miracle happens... When you wake up tomorrow, what will be different? How will you know the miracle happened?'",
            onBlur: formik.handleBlur,
            value: formik.values?.miracleQuestionResponse ?? "",
            onChange: formik.handleChange,
          }}
        />
        <Text variant="caption" color="#74797B" mt={0.5}>
          Focus on specific, observable, behavioral indicators
        </Text>
      </Grid>

      {/* Exceptions & Existing Solutions */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Exceptions & Existing Solutions</CustomTitle>
      </Flex>
      <Grid size={12}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Exceptions Identified",
            name: "exceptionsIdentified",
            placeholder:
              "Times when the problem doesn't happen or is less severe, what's different in those moments",
            onBlur: formik.handleBlur,
            value: formik.values?.exceptionsIdentified ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid size={12}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Coping Questions",
            name: "copingQuestions",
            placeholder:
              "'How have you managed to cope? What keeps you going? How have things not gotten worse?'",
            onBlur: formik.handleBlur,
            value: formik.values?.copingQuestions ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid size={12}>
        <CustomTextArea
          label="What's Already Working"
          rows={3}
          name="whatsAlreadyWorking"
          placeholder="Strengths, skills, resources already being used successfully"
          onBlur={formik.handleBlur}
          value={formik.values?.whatsAlreadyWorking ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Scaling Questions */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Scaling Questions</CustomTitle>
      </Flex>
      <Grid size={12}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Scaling Question Used",
            name: "scalingQuestionUsed",
            placeholder:
              "'On a scale of 0-10, where 10 is [preferred future] and 0 is the worst things have been, where are you today?'",
            onBlur: formik.handleBlur,
            value: formik.values?.scalingQuestionUsed ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Current Rating (0-10)",
            name: "currentRating",
            placeholder: "-",
            onBlur: formik.handleBlur,
            value: formik.values?.currentRating ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Previous Session Rating",
            name: "previousSessionRating",
            placeholder: "If applicable",
            onBlur: formik.handleBlur,
            value: formik.values?.previousSessionRating ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid size={12}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Indicators of Progress",
            name: "indicatorsOfProgress",
            placeholder:
              "'What tells you you're at a [X] and not lower? What would be different at [X+1]? What small step would move you up the scale?'",
            onBlur: formik.handleBlur,
            value: formik.values?.indicatorsOfProgress ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>

      {/* Strengths, Resources & Competencies */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Strengths, Resources & Competencies</CustomTitle>
      </Flex>
      <Grid size={12}>
        <CustomTextArea
          label="Client Strengths & Resources"
          rows={3}
          name="clientStrengthsAndResources"
          placeholder="Personal strengths, support systems, past successes, resilience factors"
          onBlur={formik.handleBlur}
          value={formik.values?.clientStrengthsAndResources ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid size={12}>
        <CustomTextArea
          label="Compliments Offered"
          rows={3}
          name="complimentsOffered"
          placeholder="Direct and indirect compliments recognizing client's efforts and strengths"
          onBlur={formik.handleBlur}
          value={formik.values?.complimentsOffered ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Small Changes & Action Steps */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Small Changes & Action Steps</CustomTitle>
      </Flex>
      <Grid size={12}>
        <CustomTextArea
          label="Small Changes Noticed"
          rows={3}
          name="smallChangesNoticed"
          placeholder="Incremental improvements, positive differences since last session"
          onBlur={formik.handleBlur}
          value={formik.values?.smallChangesNoticed ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid size={12}>
        <CustomTextArea
          label="Between-Session Tasks"
          rows={3}
          name="betweenSessionTasks"
          placeholder="Observational tasks, behavioral experiments, 'do more of what works'"
          onBlur={formik.handleBlur}
          value={formik.values?.betweenSessionTasks ?? ""}
          onChange={formik.handleChange}
        />
        <Text variant="caption" color="#74797B" mt={0.5}>
          Examples: Notice when problem doesn't occur; Do something different;
          Predict and observe
        </Text>
      </Grid>
      <Grid size={12}>
        <CustomTextArea
          label="Next Steps/Actions"
          rows={3}
          name="nextStepsActions"
          placeholder="Concrete, small steps client will take before next session"
          onBlur={formik.handleBlur}
          value={formik.values?.nextStepsActions ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Session Goals & Achievement */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Session Goals & Achievement</CustomTitle>
      </Flex>
      <Grid size={12}>
        <CustomTextArea
          label="Goals Set for This Session"
          rows={3}
          name="goalsSetForThisSession"
          placeholder="What client hoped to accomplish in this session"
          onBlur={formik.handleBlur}
          value={formik.values?.goalsSetForThisSession ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid size={12}>
        <CustomTextArea
          label="Progress Toward Session Goals"
          rows={3}
          name="progressTowardSessionGoals"
          placeholder="Extent to which session goals were met"
          onBlur={formik.handleBlur}
          value={formik.values?.progressTowardSessionGoals ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Client Response & Progress */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Client Response & Progress</CustomTitle>
      </Flex>
      <Grid size={12}>
        <CustomTextArea
          label="Client Response to Session"
          rows={3}
          name="clientResponseToSession"
          placeholder="Client's engagement, hopefulness, confidence in ability to change"
          onBlur={formik.handleBlur}
          value={formik.values?.clientResponseToSession ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Clinical Impression & Plan */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Clinical Impression & Plan</CustomTitle>
      </Flex>
      <Grid size={12}>
        <CustomTextArea
          label="Clinical Impression"
          rows={3}
          name="clinicalImpressions"
          placeholder="Overall assessment, solution-building progress"
          onBlur={formik.handleBlur}
          value={formik.values?.clinicalImpressions ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid size={12}>
        <CustomTextArea
          label="Plan for Next Session"
          rows={3}
          name="planForNextSession"
          placeholder="Review progress, build on what's working, scale questions, consider termination readiness"
          onBlur={formik.handleBlur}
          value={formik.values?.planForNextSession ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid size={12}>
        <CustomTextArea
          label="Risk Assessment"
          rows={3}
          name="riskAssessment"
          placeholder="Safety concerns, suicidal/homicidal ideation assessment"
          onBlur={formik.handleBlur}
          value={formik.values?.riskAssessment ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Clinical Diagnoses */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Clinical Diagnoses</CustomTitle>
      </Flex>
      <Grid size={12}>
        <Flex flexDirection="row" gap={2} flexWrap="wrap">
          <IcdCodesField formik={formik} />
          <CptCodesField formik={formik} />
        </Flex>
      </Grid>

      {/* Psychoeducation Session Required — rendered by parent Psychotherapy.tsx to avoid duplication */}
      {/* <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              name="psychoeducationSessionRequired"
              checked={!!formik.values?.psychoeducationSessionRequired}
              onChange={(e) =>
                formik.setFieldValue(
                  "psychoeducationSessionRequired",
                  e.target.checked
                )
              }
            />
          }
          label={
            <Text variant="body1" fontWeight={500}>
              Psychoeducation Session Required?
            </Text>
          }
        />
      </Grid> */}
    </>
  );
};

export default SolutionFocusedTherapyForm;
